import { useMemo, Fragment } from 'react';
import isUrl from 'validator/es/lib/isURL';
import { TeamChatTypes } from '@frontend/api-team-chat';
import { SmartLink, splitAndRetainDelimiters } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import { CheckboxField, useFormField, Text } from '@frontend/design-system';
import { MIN_MODAL_WIDTH } from '../../../constants';
import { useMessageContent } from '../../../hooks/message-parsing.hooks';
import { useTeamChatSelector } from '../../../providers/team-chat.provider';
import { getUserFullName } from '../../../utils';
import { MentionRenderer } from './mention-renderer';
import { messageBodyTextStyles } from './message-body-text.styles';

export const MessageBodyText = ({
  message,
  mentionedUsers,
}: {
  message: string;
  mentionedUsers?: TeamChatTypes.User[];
}) => {
  const { t } = useTranslation('team-chat');
  const { allDelimiters, currentUser } = useMessageContent(message, mentionedUsers);

  const noShowFormFieldProps = useFormField({ type: 'checkbox', value: false });
  const { setNoShowLinksSecurityModal } = useTeamChatSelector((ctx) => ctx.cache);
  const hideLinksSecurityModal = useTeamChatSelector((ctx) => ctx.hideLinksSecurityModal);

  const nodes = useMemo(() => {
    const textSegments = splitAndRetainDelimiters(message, allDelimiters);
    return textSegments.map((segment, index) => {
      const isMention = segment.startsWith('@');
      const isLink = isUrl(segment);

      if (isLink) {
        const onClickConfirm = () => {
          if (noShowFormFieldProps.value) {
            setNoShowLinksSecurityModal();
          }
        };

        return (
          <SmartLink
            key={`link-${index}`}
            text={segment}
            index={index}
            urlProps={{
              ...(!hideLinksSecurityModal && {
                confirmationModalProps: {
                  title: t(`You’re leaving Weave App`),
                  message: t(
                    `This link will take you to an external website. If you trust this link, tap on Open Link to continue.`
                  ),
                  confirmLabel: t('Open Link'),
                  confirmTrackingId: 'team-chat-2.0-links-security-conversation-modal-confirmation-button',
                  cancelTrackingId: 'team-chat-2.0-links-security-conversation-modal-cancel-button',
                  children: (
                    <CheckboxField
                      name={t('No Show checkbox')}
                      label={t(`Don’t show this message again`)}
                      labelColor='subdued'
                      trackingId='team-chat-2.0-skip-links-security-confirmation-modal-button'
                      {...noShowFormFieldProps}
                    />
                  ),
                  onConfirm: onClickConfirm,
                  maxWidth: MIN_MODAL_WIDTH,
                },
              }),
            }}
          />
        );
      }

      if (isMention && Array.isArray(mentionedUsers)) {
        const mentionUser = mentionedUsers.find(
          (user) => `@${getUserFullName(user.firstName, user.lastName)}` === segment
        );

        return (
          <MentionRenderer
            key={`mention-${index}`}
            mentionText={segment}
            currentUser={currentUser}
            index={index}
            mentionUser={mentionUser}
          />
        );
      }

      return <Fragment key={`text-${index}`}>{segment}</Fragment>;
    });
  }, [
    message,
    allDelimiters,
    currentUser,
    mentionedUsers,
    t,
    noShowFormFieldProps,
    hideLinksSecurityModal,
    setNoShowLinksSecurityModal,
  ]);

  return <Text css={messageBodyTextStyles.container}>{nodes}</Text>;
};
